import React from 'react';

/**
 * Accessibility behavior for hand-rolled modal/dialog wrappers.
 *
 * When `isOpen` flips to true, this hook:
 *   1. Remembers the element that had focus before the dialog opened.
 *   2. Moves focus to `initialFocusRef.current` (when provided) or the first
 *      focusable descendant of `rootRef.current` — falling back to the root
 *      element itself if no focusables are present.
 *   3. Installs a capturing `keydown` listener on `document` that:
 *        - Closes the dialog on `Escape` (and stops propagation so layered
 *          parent listeners do not see it too).
 *        - Traps `Tab` / `Shift+Tab` inside the dialog by wrapping at both
 *          ends of the focusable list.
 *   4. On unmount or when `isOpen` flips back to false, restores focus to the
 *      previously remembered element.
 *
 * Behavior intentionally matches what Radix Dialog gives us for free, so we
 * can use it for the dozens of hand-rolled web modals across the apps
 * without forcing a layout rewrite.
 *
 * Notes:
 *   - The listener is registered in the capture phase so it wins over any
 *     bubble-phase handlers (e.g. the global Cmd-K listener).
 *   - If `disabled` is true the hook is a no-op (useful for sub-sheets that
 *     temporarily disable the parent dialog's ESC handler).
 */
export interface UseDialogA11yOptions {
  isOpen: boolean;
  onClose: () => void;
  /** Element to receive focus when the dialog opens. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** When true, the hook does nothing. Defaults to false. */
  disabled?: boolean;
  /** When true, ESC is NOT intercepted (the caller handles it). Defaults to false. */
  ignoreEscape?: boolean;
  /** When true, focus trap is NOT applied. Defaults to false. */
  ignoreFocusTrap?: boolean;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

function getFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return true;
  });
}

export function useDialogA11y(
  rootRef: React.RefObject<HTMLElement | null>,
  {
    isOpen,
    onClose,
    initialFocusRef,
    disabled = false,
    ignoreEscape = false,
    ignoreFocusTrap = false,
  }: UseDialogA11yOptions,
): void {
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    if (!isOpen || disabled) return;

    const previouslyFocused =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;

    const root = rootRef.current;

    const focusInitial = () => {
      const explicit = initialFocusRef?.current;
      if (explicit && typeof explicit.focus === 'function') {
        explicit.focus();
        return;
      }
      const focusables = getFocusableElements(root);
      if (focusables.length > 0) {
        focusables[0].focus();
        return;
      }
      if (root && typeof root.focus === 'function') {
        // Ensure the root is focusable so screen readers announce it.
        if (!root.hasAttribute('tabindex')) {
          root.setAttribute('tabindex', '-1');
        }
        root.focus();
      }
    };

    // Defer focus to the next frame so portaled / animated children are mounted.
    const focusTimer = window.setTimeout(focusInitial, 0);

    const onKey = (event: KeyboardEvent) => {
      if (!rootRef.current) return;
      if (event.key === 'Escape' && !ignoreEscape) {
        event.stopPropagation();
        // Don't preventDefault: some browsers (Firefox) use ESC to cancel composition / leave fullscreen.
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || ignoreFocusTrap) return;

      const focusables = getFocusableElements(rootRef.current);
      if (focusables.length === 0) {
        event.preventDefault();
        rootRef.current.focus?.();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !rootRef.current.contains(active)) {
          last.focus();
          event.preventDefault();
        }
      } else if (active === last || !rootRef.current.contains(active)) {
        first.focus();
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', onKey, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey, true);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        // Skip restoration when the previous element is no longer in the document.
        if (document.contains(previouslyFocused)) {
          try {
            previouslyFocused.focus();
          } catch {
            // Some elements (svg, etc.) can throw; restoration is best-effort.
          }
        }
      }
    };
  }, [isOpen, disabled, ignoreEscape, ignoreFocusTrap, initialFocusRef, rootRef]);
}

export default useDialogA11y;
